import { createApp, defineAsyncComponent, watch } from 'vue'

import {
  getFeatureFlags,
  initializeVue as initLaunchDarkly,
} from '@sennder/senn-node-feature-flags-frontend'
import { envOverrides } from '@sennder/shell-utilities'

import router from '@/router'
import { loadUserData } from '@/store'
import { ENV, LAUNCH_DARKLY_KEY } from '@/common/config'
import { logger } from '@/services/logger/loggers'
import { cookiesService } from '@/services/CookiesService'
import { startTracking } from '@/services/tracking'
import { getLaunchDarklyAnonymousContext } from './services/launchDarkly'
import { translationProvider } from '@/services/translationProvider'
import { microfrontends } from './config/microfrontends'
import { initRouter } from './router/routes'
import { auth, loadAuthRemoteFunction } from '@/modules/auth'
import { initSessionReplay, stopSessionReplay } from '@/services/analytics'
import { GTMVuePlugin } from '@sennder/senn-node-google-tags'
import { gtManager } from '@/plugins/gtManager'
import { i18n } from '@/services/i18n'

function checkCookieConsent() {
  try {
    const consent = cookiesService.didConsentAll
    if (consent) {
      startTracking()
    }
  } catch (error) {
    logger.error(
      `[otters-shell - checkCookieConsent] Error checking cookie consent`,
      { error }
    )
  }
}

const bootPromises: Promise<void>[] = []
let userDataPromise: Promise<void> | undefined = undefined

async function start() {
  checkCookieConsent()

  try {
    window.onerror = function (message, url, line, column, error) {
      logger.error('[shippers - global error]', { error })
    }

    const authFnPromise = loadAuthRemoteFunction()

    if (ENV === 'dev') {
      envOverrides.load({
        legacyModules: Object.values(microfrontends),
        logger,
      })
    }

    if (!LAUNCH_DARKLY_KEY) {
      throw new Error('LAUNCH_DARKLY_KEY not found')
    }

    bootPromises.push(
      initLaunchDarkly(LAUNCH_DARKLY_KEY, getLaunchDarklyAnonymousContext()),
      translationProvider.initialize()
    )

    await authFnPromise
    if (await auth.value?.isAuthenticated()) {
      userDataPromise = loadUserData()
    }
  } catch (error) {
    logger.error(`[shippers] shell init`, { error })
  } finally {
    initRouter()

    await userDataPromise
    const app = createApp(defineAsyncComponent(() => import('./App.vue')))
      .use(router)
      .use(i18n)
      .use(GTMVuePlugin, {
        gtManager,
        vueRouter: router,
      })

    const { unmount } = app

    const stopWatchAmplitudeReplayFlag = watch(
      () => getFeatureFlags()['ENABLE_AMPLITUDE_SESSION_REPLAY'],
      (enable: boolean) => {
        if (enable) {
          initSessionReplay()
        } else {
          stopSessionReplay()
        }
      },
      {
        immediate: true,
      }
    )

    app.unmount = (...args) => {
      unmount(...args)
      stopWatchAmplitudeReplayFlag()
    }

    await Promise.allSettled(bootPromises)
    await router.isReady()
    app.mount('#app')
  }
}

start()
